// -----------------------------------------------------------------------------
// AttendedFamilyHero
//
// Shared lead-summary hero card used by both attended-family popup modals:
//   • ConsultationAttendedAppointment (CAA) — Attended popup
//   • FollowUpV2 — V2 follow-up modal
//
// Extracts the headerNode that was previously copy-pasted in both files
// (CAA:481-661, V2:601-861). Both callers now import this component and
// diverge ONLY via props, not via duplicated JSX trees.
//
// Props:
//   followLead          — lead object from Redux (primary data source)
//   leadId              — numeric lead ID (for AssignedUsersShow)
//   leadName            — formatted "First Last" string (pre-computed by caller)
//   treatment           — treatment name string or '—'
//   treatmentDateTime   — formatted datetime string or null/'' (appointment chip)
//   treatmentAssignees  — array of practitioner objects ({ user, role })
//   assigneeSelected    — array of lead assignee objects
//   setAssigneeSelected — setter for assigneeSelected (for AssignedUsersShow)
//   price               — numeric price or null
//   statusLabel         — chip label: "Attended" (CAA) | lead status string (V2)
//   statusColor         — chip color: '#8b5cf6' (CAA) | '#078DEE' (V2)
//   statusIcon          — Iconify icon string for the status chip (optional)
//   onEdit              — optional: renders an edit (pencil) button in top-right
//   onAssignPractitioner — optional: renders a pencil on the Practitioner row in
//                          the expanded panel (CAA only; V2 passes null)
//   dbgSx               — debug outline helper (per-caller closure)
//   DbgTag              — debug label component (per-caller closure)
//   extraChips          — optional JSX slot: additional chips after treatmentDateTime
//                         chip (V2 uses this for the "V2 checklist" grey chip)
//   children            — optional JSX slot: rendered below the chip row (V2 uses
//                         this for the preferred-contact row)
//   compact             — optional bool (default false). When true, the hero
//                         collapses to a one-row variant: status chip + name +
//                         treatment + datetime + extraChips + price ONLY. The
//                         avatar stacks, `children` slot, expand chevron, and
//                         expandable detail panel are all hidden. Reduced
//                         padding + smaller name/price typography to give the
//                         body underneath (e.g. V2's booking wizard) more
//                         vertical screen space. FollowUpV2 sets this true when
//                         the Book appointment chip is active; Attended doesn't
//                         pass it → defaults false → no visual change.
//                         Added 2026-06-07.
// -----------------------------------------------------------------------------
/* eslint-disable react/no-array-index-key */
import PropTypes from 'prop-types';
import { useState } from 'react';
import {
    Box, Stack, Typography, Chip, Collapse, IconButton, Tooltip, Zoom,
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import resolveLeadTreatmentPrice from 'src/utils/resolveLeadTreatmentPrice';
import DemoBlur, { piiProps, piiTitle } from '../DemoBlur';
import { AssignUserAvatar } from '../custom-avatar';
import AssignedUsersShow from '../AssignedUsersShow';
import AssignedToLead from '../AssignedToLead';

export default function AttendedFamilyHero({
    followLead,
    leadId,
    leadName,
    treatment,
    treatmentDateTime,
    treatmentAssignees,
    assigneeSelected,
    setAssigneeSelected,
    price,
    statusLabel,
    statusColor,
    statusIcon,
    onEdit,
    onAssignPractitioner,
    dbgSx,
    DbgTag,
    extraChips,
    statusRowExtras,
    priceBelowNode,
    hidePriceLabel,
    onAssigneePersist,
    children,
    compact,
}) {
    const [infoExpanded, setInfoExpanded] = useState(false);

    const resolvedPrice = price != null
        ? price
        : resolveLeadTreatmentPrice({ followLead });

    return (
        <Box sx={{
            px: 3,
            py: compact ? 1 : 2,
            mt: compact ? 0.5 : 1,
            mb: compact ? 1 : 2,
            bgcolor: '#ffffff',
            borderRadius: '12px',
            // Smooth height transition between full ↔ compact when caller flips `compact`
            // (e.g. V2 user clicks Book appointment chip). transition is on padding so
            // the size diff animates rather than snapping.
            transition: 'padding 220ms cubic-bezier(0.32, 0.72, 0, 1), margin 220ms cubic-bezier(0.32, 0.72, 0, 1)',
            ...dbgSx('#2962FF'),
        }}>
            <DbgTag color="#2962FF" label={compact ? 'HEADER (compact)' : 'HEADER'} />

            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                {/* ── Left column ── */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* Status chip row */}
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                        <Chip
                            size="small"
                            {...(statusIcon ? {
                                icon: <Iconify icon={statusIcon} width={13} height={13} sx={{ color: `${statusColor} !important`, ml: '6px !important' }} />,
                            } : {})}
                            label={statusLabel}
                            sx={{
                                height: 22,
                                fontSize: '11px',
                                fontWeight: 600,
                                bgcolor: `${statusColor}1A`, // 10% alpha
                                color: statusColor,
                                letterSpacing: '-0.005em',
                                border: 'none',
                                '& .MuiChip-label': { px: 0.75 },
                            }}
                        />
                        {/* Optional caller-supplied chips placed inline with the status chip
                            (V2 uses this for source / referral context). */}
                        {statusRowExtras}
                    </Stack>

                    {/* Lead name — smaller in compact mode to free vertical space */}
                    <Typography {...piiProps} sx={{
                        fontSize: compact ? '18px' : '24px',
                        fontWeight: 700,
                        color: '#1d1d1f',
                        letterSpacing: '-0.026em',
                        lineHeight: 1.15,
                        transition: 'font-size 220ms cubic-bezier(0.32, 0.72, 0, 1)',
                    }}>
                        {leadName}
                    </Typography>

                    {/* Chip row: treatment + datetime + practitioner avatars + assignee avatars + extraChips */}
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap' }}>
                        {/* Treatment chip */}
                        {treatment && treatment !== '—' && (
                            <Chip
                                size="small"
                                icon={<Iconify icon="mdi:tooth-outline" width={13} height={13} sx={{ color: '#078DEE !important', ml: '6px !important' }} />}
                                label={treatment}
                                sx={{
                                    height: 24, fontSize: '12px', fontWeight: 500,
                                    bgcolor: 'rgba(7,141,238,0.08)', color: '#078DEE',
                                    border: 'none',
                                    '& .MuiChip-label': { px: 0.75 },
                                }}
                            />
                        )}

                        {/* Appointment datetime chip */}
                        {treatmentDateTime && (
                            <Chip
                                size="small"
                                icon={<Iconify icon="mdi:calendar-clock" width={13} height={13} sx={{ color: '#00AB55 !important', ml: '6px !important' }} />}
                                label={treatmentDateTime}
                                sx={{
                                    height: 24, fontSize: '12px', fontWeight: 500,
                                    bgcolor: 'rgba(0,171,85,0.08)', color: '#00AB55',
                                    border: 'none',
                                    '& .MuiChip-label': { px: 0.75 },
                                }}
                            />
                        )}

                        {/* Caller-supplied chips (V2: "V2 checklist" grey chip) */}
                        {extraChips}

                        {/* Practitioner avatar stack — hidden in compact mode */}
                        {!compact && treatmentAssignees?.length > 0 && (
                            <DemoBlur component="div">
                            <Tooltip
                                title={piiTitle(`Practitioner: ${treatmentAssignees.map(a => `${a?.user?.first_name || a?.first_name || ''} ${a?.user?.last_name || a?.last_name || ''}`.trim()).filter(Boolean).join(', ')}`)}
                                placement="bottom"
                                TransitionComponent={Zoom}
                                TransitionProps={{ timeout: 300 }}
                            >
                                <Stack direction="row" alignItems="center" spacing={-0.5} sx={{ ml: 0.5 }}>
                                    {treatmentAssignees.slice(0, 2).map((a, i) => (
                                        <AssignUserAvatar
                                            key={i}
                                            sx={{ height: 22, width: 22, fontSize: '10px', border: '1.5px solid white' }}
                                            src={a?.user?.avatar || ''}
                                            name={`${a?.user?.first_name || a?.first_name || ''} ${a?.user?.last_name || a?.last_name || ''}`}
                                            role={a?.role}
                                        />
                                    ))}
                                </Stack>
                            </Tooltip>
                            </DemoBlur>
                        )}

                        {/* Lead assignee avatar stack — hidden in compact mode */}
                        {!compact && assigneeSelected?.length > 0 && (
                            <Tooltip
                                title={`Assignees: ${assigneeSelected.map(a => `${a.user?.first_name || ''} ${a.user?.last_name || ''}`.trim()).filter(Boolean).join(', ')}`}
                                placement="bottom"
                                TransitionComponent={Zoom}
                                TransitionProps={{ timeout: 300 }}
                            >
                                <Stack direction="row" alignItems="center" spacing={-0.5}>
                                    {assigneeSelected.slice(0, 2).map((ele, i) => (
                                        <AssignUserAvatar
                                            key={i}
                                            sx={{ height: 22, width: 22, fontSize: '10px', border: '1.5px solid white' }}
                                            src={ele.user?.avatar || ''}
                                            name={`${ele.user?.first_name || ''} ${ele.user?.last_name || ''}`}
                                            role={ele?.role}
                                        />
                                    ))}
                                </Stack>
                            </Tooltip>
                        )}
                    </Stack>

                    {/* Caller-supplied slot below chip row (V2: preferred-contact row,
                        phone/email row, source/referral/tags pills, enquiry box).
                        Hidden in compact mode to give the body underneath more screen. */}
                    {!compact && children}
                </Box>

                {/* ── Right column: price + actions ── */}
                <Stack direction="row" alignItems="flex-start" spacing={0.5} sx={{ flexShrink: 0 }}>
                    <Box sx={{ textAlign: 'right' }}>
                        {/* "Price" label hidden when hidePriceLabel (FollowUpV2 declutter,
                            Raj 2026-06-13) — the £ value below always renders. */}
                        {!hidePriceLabel && (
                            <Typography sx={{ fontSize: '11px', color: '#6e6e73', letterSpacing: '0.02em', textTransform: 'uppercase', fontWeight: 500, mb: 0.25 }}>
                                Price
                            </Typography>
                        )}
                        <Typography
                            data-testid="attended-family-hero-price"
                            sx={{
                                fontSize: compact ? '18px' : '24px',
                                fontWeight: 700,
                                color: '#1d1d1f',
                                letterSpacing: '-0.026em',
                                lineHeight: 1.15,
                                fontVariantNumeric: 'tabular-nums',
                                transition: 'font-size 220ms cubic-bezier(0.32, 0.72, 0, 1)',
                            }}
                        >
                            {resolvedPrice == null ? '—' : `£${Number(resolvedPrice).toLocaleString('en-GB')}`}
                        </Typography>
                        {/* Optional slot beneath the price — V2 uses this for the email line so it sits
                            in the same right-column rhythm as the price, not stacked under the phone. */}
                        {priceBelowNode && (
                            <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'flex-end' }}>
                                {priceBelowNode}
                            </Box>
                        )}
                    </Box>
                    <Stack direction="column" spacing={0.25} sx={{ ml: 0.5 }}>
                        {/* Edit button — CAA only; omitted in V2 */}
                        {onEdit && (
                            <IconButton
                                size="small"
                                onClick={onEdit}
                                sx={{ color: '#6e6e73', '&:hover': { bgcolor: 'rgba(0,113,227,0.06)', color: '#0071e3' } }}
                            >
                                <Iconify icon="solar:pen-bold" width={15} height={15} />
                            </IconButton>
                        )}
                        {/* Expand/collapse chevron — hidden in compact mode (no detail to reveal) */}
                        {!compact && (
                            <IconButton
                                size="small"
                                onClick={() => setInfoExpanded((v) => !v)}
                                sx={{ color: '#6e6e73', mt: onEdit ? 0 : 2.5, '&:hover': { bgcolor: 'rgba(0,0,0,0.04)', color: '#1d1d1f' } }}
                            >
                                <Iconify icon={infoExpanded ? 'eva:chevron-up-fill' : 'eva:chevron-down-fill'} width={18} height={18} />
                            </IconButton>
                        )}
                    </Stack>
                </Stack>
            </Stack>

            {/* Expanded detail panel — phone, address, practitioner, assignees.
                Always collapsed in compact mode (no chevron is visible so the user
                can't expand it; if a stale infoExpanded=true survives the mode flip
                we still want it visually closed). */}
            <Collapse in={!compact && infoExpanded} timeout={300} unmountOnExit>
                <Box sx={{ mt: 2 }}>
                    {/* Address row. Phone intentionally NOT repeated here (Raj 2026-06-13):
                        it's already shown prominently in the header's contact column above,
                        so duplicating it in the expanded panel added noise. Only the address
                        (which is NOT in the header) is surfaced on expand. */}
                    {followLead?.lead_address && (
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                            <Typography {...piiProps} sx={{ fontSize: '13px', color: '#6e6e73', letterSpacing: '-0.01em' }}>
                                {[followLead.lead_address.address_line_1, followLead.lead_address.city, followLead.lead_address.postcode].filter(Boolean).join(', ')}
                            </Typography>
                        </Stack>
                    )}

                    {/* Practitioner + assignees detail columns */}
                    <Stack direction="row" justifyContent="space-between" flexWrap="wrap" useFlexGap sx={{ gap: 4 }}>
                        <Box sx={{ flex: 1, minWidth: 160 }}>
                            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                                <Typography sx={{ fontSize: '11px', color: '#6e6e73', letterSpacing: '-0.005em' }}>Practitioner</Typography>
                                {/* Practitioner edit pencil — CAA only */}
                                {onAssignPractitioner && (
                                    <IconButton size="small" sx={{ p: 0.25 }} onClick={onAssignPractitioner}>
                                        <Iconify width={12} height={12} icon="solar:pen-bold" sx={{ color: '#6e6e73' }} />
                                    </IconButton>
                                )}
                            </Stack>
                            <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#1d1d1f', letterSpacing: '-0.01em' }}>
                                <DemoBlur>
                                {(() => {
                                    const names = (treatmentAssignees || []).map(a =>
                                        `${a?.user?.first_name || a?.first_name || ''} ${a?.user?.last_name || a?.last_name || ''}`.trim()
                                    ).filter(Boolean);
                                    if (names.length === 0) return '—';
                                    if (names.length === 1) return names[0];
                                    return `${names[0]} + ${names.length - 1}`;
                                })()}
                                </DemoBlur>
                            </Typography>
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 160 }}>
                            <Typography sx={{ fontSize: '11px', color: '#6e6e73', letterSpacing: '-0.005em', mb: 0.5 }}>Assignees</Typography>
                            {onAssigneePersist ? (
                                /* Unified picker (Raj 2026-06-13): use the same AssignedToLead
                                   round-head toggle UI as the checklist assignee row. Toggling a
                                   head calls onAssigneePersist, which updates local state AND saves
                                   the lead's assignees — same control across the board. The label
                                   is rendered above, so AssignedToLead is passed no label. */
                                <AssignedToLead
                                    staffRole="assignee"
                                    assignToState={assigneeSelected}
                                    setAssignTo={onAssigneePersist}
                                />
                            ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {assigneeSelected?.length > 0
                                        ? assigneeSelected.slice(0, 4).map((ele, index) => (
                                            <Tooltip
                                                key={index}
                                                title={`${ele.user?.first_name || ''} ${ele.user?.last_name || ''}`}
                                                placement="bottom"
                                                TransitionComponent={Zoom}
                                                TransitionProps={{ timeout: 300 }}
                                            >
                                                <AssignUserAvatar
                                                    sx={{ height: 28, width: 28, fontSize: '11px' }}
                                                    src={ele.user?.avatar || ''}
                                                    name={`${ele.user?.first_name || ''} ${ele.user?.last_name || ''}`}
                                                    role={ele?.role}
                                                />
                                            </Tooltip>
                                        ))
                                        : <Typography sx={{ fontSize: '13px', color: '#6e6e73' }}>—</Typography>
                                    }
                                    {assigneeSelected?.length > 4 && (
                                        <Typography sx={{ fontSize: '11px', color: '#6e6e73', ml: 0.5 }}>
                                            +{assigneeSelected.length - 4}
                                        </Typography>
                                    )}
                                    <AssignedUsersShow
                                        assigneeSelected={assigneeSelected}
                                        setAssigneeSelected={setAssigneeSelected}
                                        leadId={leadId}
                                        compact
                                    />
                                </Box>
                            )}
                        </Box>
                    </Stack>
                </Box>
            </Collapse>
        </Box>
    );
}

AttendedFamilyHero.propTypes = {
    followLead:           PropTypes.object,
    leadId:               PropTypes.number,
    leadName:             PropTypes.string,
    treatment:            PropTypes.string,
    treatmentDateTime:    PropTypes.string,
    treatmentAssignees:   PropTypes.array,
    assigneeSelected:     PropTypes.array,
    setAssigneeSelected:  PropTypes.func,
    price:                PropTypes.number,
    statusLabel:          PropTypes.string.isRequired,
    statusColor:          PropTypes.string.isRequired,
    statusIcon:           PropTypes.string,
    onEdit:               PropTypes.func,
    onAssignPractitioner: PropTypes.func,
    dbgSx:                PropTypes.func.isRequired,
    DbgTag:               PropTypes.elementType.isRequired,
    extraChips:           PropTypes.node,
    statusRowExtras:      PropTypes.node,
    priceBelowNode:       PropTypes.node,
    hidePriceLabel:       PropTypes.bool,
    onAssigneePersist:    PropTypes.func,
    children:             PropTypes.node,
    compact:              PropTypes.bool,
};

AttendedFamilyHero.defaultProps = {
    followLead:           null,
    leadId:               null,
    leadName:             '',
    treatment:            '—',
    treatmentDateTime:    null,
    treatmentAssignees:   [],
    assigneeSelected:     [],
    setAssigneeSelected:  null,
    price:                null,
    statusIcon:           null,
    onEdit:               null,
    onAssignPractitioner: null,
    extraChips:           null,
    statusRowExtras:      null,
    priceBelowNode:       null,
    hidePriceLabel:       false,
    onAssigneePersist:    null,
    children:             null,
    compact:              false,
};
